import React, { useState } from "react";
import { Footer } from "react-day-picker";
import { ScrollArea } from "@ui/scroll-area";
import { useB2B } from "@modules/content/B2BContext";
import { MainPlayer } from "@components/layouts/b2c/layout/MainPlayer";
import {
  setIsCreditModalOpen,
  setIsSubscriptionModalOpen,
  useAppSelector,
  useSuccessStatus,
} from "@lib/stores";
import { db } from "@modules/local/db";
import { useAppLiveQuery } from "@modules/local/hooks";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@ui/resizable";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import NavBar from "./b2b/NavBar";
import SideBar from "./b2b/SideBar";
import CommonAlert from "@components/layouts/b2c/common/CommonAlert";
import { useAlertHandler } from "@utils/hooks/useAlertHandler";
import { useLanguage } from "@modules/localizations/LanguageContext";
import SuccessAlert from "@components/layouts/b2c/common/SuccessAlert";
import { SubscriptionModal } from "./subscription/SubscriptionModal";
import CreditModal from "./subscription/credit/CreditModal";
import { SVG } from "@components/items/common/SVG";

const MainLayout = ({ children }: { children: React.ReactNode }) => {
  // TODO: const pathname = usePathname();
  // TODO: const [activeTab, setActiveTab] = useState(pathname?.split("/")[1] || "home");
  const [subType, setSubType] = useState<string>("freemium");
  const [isPlayerOn, setIsPlayerOn] = useState<boolean>(true);

  const { alert } = useAlertHandler();

  const { isSuccess, message } = useSuccessStatus();

  const userId = useAppSelector((state) => state.auth.user.id ?? "");

  const isSubscriptionModalOpen = useAppSelector(
    ({ common }) => common.isSubscriptionModalOpen
  );

  const isCreditModalOpen = useAppSelector(
    ({ common }) => common.isCreditModalOpen
  );

  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(userId),
    [userId]
  );

  const navigate = useAppNavigate();

  const { b2B, toggleB2B } = useB2B();

  const { language, toggleLanguage } = useLanguage();

  return (
    <>
      {alert.show && alert.for === "page" && <CommonAlert alert={alert} />}
      {isSuccess && <SuccessAlert message={message} />}
      <div className="flex h-full w-full items-center justify-start overflow-hidden">
        <div className="h-full min-w-[120px]">
          <SideBar
            isB2B={b2B?.isB2B}
            Logo={
              b2B?.isB2B && b2B.data[0]?.logoReference ? (
                <img
                  src={getPublicMediaUrl(b2B.data[0]?.logoReference)}
                  alt="b2b-logo"
                  className="h-[50px] w-[50px] rounded-[8px]"
                />
              ) : language === "ar" ? (
                <SVG
                  src="/assets/icons/logo-ar.svg"
                  preProcessor={(code: string) =>
                    code
                      .replace(
                        /(fill=)"(?!none")[^"]+"/g,
                        `fill="currentColor"`
                      )
                      .replace(
                        /(stroke=)"(?!none")[^"]+"/g,
                        `stroke="currentColor"`
                      )
                  }
                  className="text-primary dark:text-[#64A6E1]"
                />
              ) : (
                <SVG
                  src="/assets/icons/logo-en.svg"
                  preProcessor={(code: string) =>
                    code
                      .replace(
                        /(fill=)"(?!none")[^"]+"/g,
                        `fill="currentColor"`
                      )
                      .replace(
                        /(stroke=)"(?!none")[^"]+"/g,
                        `stroke="currentColor"`
                      )
                  }
                  className="h-[60px] w-[60px] text-primary dark:text-[#64A6E1]"
                />
              )
            }
            tabs={
              flow?.tabs
                ?.filter(
                  (tab) =>
                    (b2B?.isB2B ? tab.isB2B : !tab.isB2B) && !tab.isCarPlay
                )
                .map((tab) => ({
                  id: tab.id?.toString() ?? "",
                  title: tab.name ?? "",
                  iconPath: tab.iconUrl ?? "",
                  path: `/`,
                  iconType: "url",
                  onClick: () => {
                    navigate("/", {
                      tabId: tab.id ?? 0,
                    });
                  },
                })) ?? []
            }
          />
        </div>
        <div
          className="flex h-full w-full flex-col ltr:mr-auto rtl:ml-auto"
          style={{
            width: "calc(100vw - 120px)",
          }}
        >
          <div className="min-h-[100px]">
            <NavBar
              isB2bAllowed={b2B?.isB2bAllowed}
              isB2cAllowed={b2B?.isB2cAllowed}
              isB2b={b2B?.isB2B}
              onB2bChange={() => {
                toggleB2B(!b2B?.isB2B);
              }}
              language={language}
              onLanguageToggle={() => {
                toggleLanguage(language === "ar" ? "en" : "ar");
              }}
            />
          </div>
          <div className="mb-auto w-full">
            <div
              className="w-full"
              style={{
                height: "calc(100vh - 100px)",
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
      {isSubscriptionModalOpen ? (
        <SubscriptionModal
          isOpen={isSubscriptionModalOpen}
          onClose={() => {
            console.log("close");

            setIsSubscriptionModalOpen(false);
          }}
        />
      ) : null}
      {isCreditModalOpen && (
        <CreditModal
          isOpen={isCreditModalOpen}
          onClose={() => {
            setIsCreditModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default MainLayout;
